import { useEffect } from "react";
import { Link } from "react-router";
import { HIREOS_INQUIRY_URL } from "@shared/hireos-links";
import { ServiceRow } from "@/components/home/ServiceRow";
import { ScrollToServices } from "@/components/home/ScrollToServices";

const PAGE_TITLE = "Accent Productions | Sound Hire Wellington";

const services = [
  {
    title: "Public Events",
    description: "Ceremony and reception audio systems.",
    image: {
      src: "/images/image1-public-parilament.webp",
      location: "Parliament, Wellington",
      alt: "Sound hire for public event at Parliament Wellington",
    },
    href: "/services#public",
  },
  {
    title: "Corporate Events",
    description: "Conferences, presentations, and company events.",
    image: {
      src: "/images/image1-corporate.webp",
      location: "Wellington CBD, New Zealand",
      alt: "Corporate event audio setup Wellington CBD",
    },
    href: "/services#corporate",
  },
  {
    title: "Festivals",
    description: "Large-scale outdoor sound systems.",
    image: {
      src: "/images/image1-festival-cuba-st.webp",
      location: "Cuba Street, Wellington",
      alt: "Sound equipment at Cuba Dupa festival Wellington",
    },
    href: "/services#festivals",
  },
  {
    title: "Private Events",
    description: "Birthdays, anniversaries, and celebrations.",
    image: {
      src: "/images/image1-private-marton.jpeg",
      location: "Marton, New Zealand",
      alt: "Private event sound equipment hire Wellington",
    },
    href: "/services#parties",
  },
];

const stats = [
  { value: "2,000+", label: "shows delivered" },
  { value: "20 yrs", label: "in Wellington" },
  { value: "2005", label: "Newtown Festival, every year since" },
];

export default function Home() {
  useEffect(() => {
    document.title = PAGE_TITLE;
  }, []);

  return (
    <main className="min-h-screen bg-white">
      {/* Hero — fills exactly one viewport below the 5rem header: headline
          block on white, then the photograph edge to edge filling the rest,
          so the whole image (and its caption) is on screen on load. Uses svh,
          not dvh: dvh tracks the mobile URL bar collapsing on scroll, which
          resizes the image live and reads as an animation. */}
      <section className="h-[calc(100svh-5rem)] flex flex-col">
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-8 md:pt-12 pb-8 md:pb-12">
          <p className="text-sm font-medium text-gray-500 mb-4 md:mb-5">
            Sound hire, Wellington, New Zealand
          </p>
          <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tight leading-[0.95] text-gray-900 max-w-5xl">
            Sound for Wellington&apos;s biggest events
            <span className="text-orange-600">.</span>
          </h1>
          <p className="mt-5 md:mt-7 text-lg sm:text-xl md:text-2xl text-gray-600 font-medium max-w-2xl">
            PA, lighting, and crew. 2,000+ shows since 2005.
          </p>
          <div className="mt-6 md:mt-9 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-x-8">
            <a
              href={HIREOS_INQUIRY_URL}
              className="bg-orange-600 text-white px-8 py-3.5 rounded-md font-bold text-lg text-center w-full sm:w-auto"
            >
              Get a Quote
            </a>
            <ScrollToServices />
          </div>
        </div>

        <div className="relative flex-1 min-h-0">
          <img
            src="/images/image3-festival-newtown-festival.jpg"
            alt="Professional sound equipment hire at Newtown Festival Wellington - Accent Productions"
            fetchPriority="high"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <p className="absolute bottom-3 right-4 text-white text-xs sm:text-sm font-medium [text-shadow:0_1px_3px_rgba(0,0,0,0.85)]">
            Newtown Festival, Wellington
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 sm:grid-cols-3 gap-8 md:gap-16">
          {stats.map((stat) => (
            <div key={stat.value}>
              <p className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-gray-900">
                {stat.value}
              </p>
              <p className="mt-2 md:mt-3 text-gray-500 font-medium">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Services — numbered editorial rows */}
      <section id="services" className="py-16 md:py-24 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900 mb-10 md:mb-20">
            What we do<span className="text-orange-600">...</span>
          </h2>
          <div className="space-y-14 md:space-y-28">
            {services.map((service, index) => (
              <ServiceRow
                key={service.title}
                service={service}
                index={index}
              />
            ))}
          </div>
        </div>
      </section>

      {/* About teaser */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-gray-900 mb-6">
                Twenty years behind the desk
                <span className="text-orange-600">.</span>
              </h2>
              <p className="text-gray-600 mb-8 font-medium text-lg md:text-xl max-w-md">
                Newtown Festival since 2005. Cuba Dupa since 2022. Over 2,000
                events in between.
              </p>
              <Link
                to="/about"
                className="text-orange-600 font-bold cursor-pointer text-lg underline underline-offset-4"
              >
                Learn more about us →
              </Link>
            </div>
            <div className="aspect-[4/3] relative rounded-md overflow-hidden">
              <img
                src="/images/image1-public-waitangipark.webp"
                alt="Accent Productions sound equipment setup at Waitangi Park Wellington"
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <p className="absolute bottom-3 right-3 text-white text-xs font-medium [text-shadow:0_1px_3px_rgba(0,0,0,0.85)]">
                Waitangi Park, Wellington
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
